/**
 * Custom hook for fetching and managing dynamic content from the API.
 *
 * This hook provides a simple way to fetch dynamic content sections and
 * parse their JSON data with support for loading states, error handling,
 * and fallback data when content is not available.
 *
 * @example
 * ```tsx
 * // Single section with fallback
 * const { data, isLoading, error } = useDynamicContent<ScheduleItem[]>(
 *   'event_schedule',
 *   defaultSchedule
 * );
 *
 * // Multiple sections
 * const { data, isLoading } = useMultipleDynamicContent(
 *   ['event_schedule', 'activities', 'causes']
 * );
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { contentApi, ApiClientError } from '@/services/api';
import type { DynamicContent } from '@/types';

/**
 * State interface for dynamic content fetching
 */
export interface DynamicContentState<T> {
  /** The parsed content data or fallback */
  data: T;
  /** Raw dynamic content from API */
  rawContent: DynamicContent | null;
  /** Whether the content is currently loading */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether data came from API (true) or is fallback (false) */
  isFromApi: boolean;
  /** Refetch the content */
  refetch: () => Promise<void>;
}

/**
 * Parse JSON content from DynamicContent.htmlContent field
 * Returns fallback data if parsing fails or content is empty
 */
function parseContentData<T>(
  content: DynamicContent | null,
  fallback: T
): { data: T; isFromApi: boolean } {
  if (!content?.htmlContent || !content.isActive) {
    return { data: fallback, isFromApi: false };
  }

  try {
    const parsed = JSON.parse(content.htmlContent) as T;
    // Validate that parsed data is not empty
    if (Array.isArray(parsed) && parsed.length === 0) {
      return { data: fallback, isFromApi: false };
    }
    return { data: parsed, isFromApi: true };
  } catch {
    // If htmlContent is not JSON, return fallback
    return { data: fallback, isFromApi: false };
  }
}

/**
 * Hook for fetching a single dynamic content section
 *
 * @param sectionKey - The key identifying the content section (e.g., 'event_schedule')
 * @param fallbackData - Default data to use if API fetch fails or content is empty
 * @returns State object with data, loading, error, and refetch function
 */
export function useDynamicContent<T>(
  sectionKey: string,
  fallbackData: T
): DynamicContentState<T> {
  const [data, setData] = useState<T>(fallbackData);
  const [rawContent, setRawContent] = useState<DynamicContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromApi, setIsFromApi] = useState(false);
  // `fallbackData` is captured in a ref so an unstable identity (new object
  // literal each render) doesn't retrigger `fetchContent` and create an
  // infinite render loop. We always read .current at call time.
  const fallbackRef = useRef(fallbackData);
  fallbackRef.current = fallbackData;

  const fetchContent = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const content = await contentApi.get(sectionKey);
      setRawContent(content);

      const { data: parsedData, isFromApi: fromApi } = parseContentData<T>(
        content,
        fallbackRef.current,
      );

      setData(parsedData);
      setIsFromApi(fromApi);
    } catch (err) {
      // Handle 404 gracefully - content may not exist yet
      if (err instanceof ApiClientError && err.status === 404) {
        setData(fallbackRef.current);
        setIsFromApi(false);
        setRawContent(null);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch content';
        setError(errorMessage);
        setData(fallbackRef.current);
        setIsFromApi(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [sectionKey]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return {
    data,
    rawContent,
    isLoading,
    error,
    isFromApi,
    refetch: fetchContent,
  };
}

/**
 * State interface for multiple dynamic content sections
 */
export interface MultipleDynamicContentState {
  /** Map of section key to raw content */
  data: Record<string, DynamicContent>;
  /** Whether content is currently loading */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Refetch all content */
  refetch: () => Promise<void>;
  /** Get parsed data for a specific section with fallback */
  getSection: <T>(sectionKey: string, fallback: T) => T;
}

/**
 * Hook for fetching multiple dynamic content sections at once
 *
 * @param sectionKeys - Array of section keys to fetch
 * @returns State object with data map, loading, error, and helper functions
 */
export function useMultipleDynamicContent(
  sectionKeys: string[]
): MultipleDynamicContentState {
  const [data, setData] = useState<Record<string, DynamicContent>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    if (sectionKeys.length === 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const content = await contentApi.getMultiple(sectionKeys);
      setData(content);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch content';
      setError(errorMessage);
      setData({});
    } finally {
      setIsLoading(false);
    }
  }, [sectionKeys]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const getSection = useCallback(
    <T,>(sectionKey: string, fallback: T): T => {
      const content = data[sectionKey];
      const { data: parsedData } = parseContentData<T>(content, fallback);
      return parsedData;
    },
    [data]
  );

  return {
    data,
    isLoading,
    error,
    refetch: fetchContent,
    getSection,
  };
}

/**
 * Content section keys used throughout the application
 */
export const CONTENT_SECTION_KEYS = {
  EVENT_SCHEDULE: 'event_schedule',
  ACTIVITIES: 'activities',
  CHARITABLE_CAUSES: 'charitable_causes',
  ABOUT_EVENT: 'about_event',
  HERO_CONTENT: 'hero_content',
  VENUE_INFO: 'venue_info',
} as const;

export type ContentSectionKey = (typeof CONTENT_SECTION_KEYS)[keyof typeof CONTENT_SECTION_KEYS];

/**
 * Loading skeleton component for content sections
 */
export function ContentLoadingSkeleton({ className = '' }: { className?: string }) {
  return (
    <div className={`animate-pulse ${className}`}>
      <div className="h-8 bg-white/5 rounded w-1/3 mb-4" />
      <div className="space-y-3">
        <div className="h-4 bg-white/5 rounded w-full" />
        <div className="h-4 bg-white/5 rounded w-5/6" />
        <div className="h-4 bg-white/5 rounded w-4/6" />
      </div>
    </div>
  );
}

export default useDynamicContent;
